import type { ActiveDiscount, Sale } from '../../services/api'
import type { Workspace } from '../../stores/workspaceStore'
import { discountKey, effectiveMentionRights, salesByDiscountKey, speakability } from './discountWording'
import { isQuietHour, resolveQuietWindow } from '@marva/shared/quiet-window'

/**
 * "At a glance" on Limits (MAR-385) — five numbers read off data the screen already holds, never
 * a read of its own that could block the tab. Each value is a string a merchant can check against
 * the section it summarises; anything not known yet is `null`, which the aside renders as "—".
 */

export type GrantsReadState = 'pending' | 'ok' | 'failed'

/**
 * What the mention-grant card knows, and the store it knows it about — the card's own state
 * shape (`MentionGrantsSection`), shared so the aside counts from the very same read.
 */
export type GrantsSnapshot = {
  workspaceId: string
  discounts: ActiveDiscount[]
  sales: Sale[]
  discountsRead: GrantsReadState
  salesRead: GrantsReadState
  discountsTruncated: boolean
  salesTruncated: boolean
}

/**
 * How many Shopify codes Marva may bring up right now: CODE discounts whose grant is in force,
 * by the card's own rules (`speakability`, `effectiveMentionRights`). `null` — "we cannot say" —
 * when either read failed, is pending, or stopped at its cap: a short list would undercount, and
 * an undercount on a margin decision is a claim, not a summary.
 */
export const liveGrantCount = (snap: GrantsSnapshot | null, nowIso: string): number | null => {
  if (!snap || snap.discountsRead !== 'ok' || snap.salesRead !== 'ok') return null
  if (snap.discountsTruncated || snap.salesTruncated) return null
  const byKey = salesByDiscountKey(snap.sales)
  const read = { landed: true, complete: true }
  return snap.discounts.filter(
    (d) =>
      speakability(d).kind === 'code' &&
      effectiveMentionRights(byKey.get(discountKey(d)), read, nowIso).rights.kind === 'active'
  ).length
}

/** 9 → "9am", 21 → "9pm", 0 → "12am". */
export const shortHour = (h: number): string => `${h % 12 === 0 ? 12 : h % 12}${h < 12 ? 'am' : 'pm'}`

const EXPIRY_LABELS: Record<number, string> = { 24: '24 hours', 48: '48 hours', 72: '72 hours', 168: '7 days' }
const PERIOD_WORDS: Record<string, string> = { daily: 'a day', weekly: 'a week', monthly: 'a month' }

export interface GlanceRow {
  label: string
  value: string | null
}

/**
 * The hours a text may not START, as the SMS gate applies them: the merchant's `smsConfig` window
 * (or the default) UNIONED with the fixed 9pm–8am floor — `resolveQuietWindow('sms', …)` and
 * `isQuietHour`, the same resolver and predicate `checkCanSendSms` evaluates through. Rendered as
 * the union's contiguous runs, earliest start first: "9pm–8am" when the merchant's window sits
 * inside the floor, "9pm–9am" when it extends it, "1pm–3pm and 9pm–8am" when it is apart from it.
 */
export const textQuietHours = (workspace: { smsConfig?: unknown } | null | undefined): string => {
  const { window, floor } = resolveQuietWindow('sms', { workspace })
  const windows = floor ? [window, floor] : [window]
  const quiet = Array.from({ length: 24 }, (_, h) => windows.some((w) => isQuietHour(w, h)))
  if (quiet.every(Boolean)) return 'All day'
  const runs: string[] = []
  for (let h = 0; h < 24; h++) {
    if (!quiet[h] || quiet[(h + 23) % 24]) continue
    let end = h
    while (quiet[end % 24]) end++
    runs.push(`${shortHour(h)}–${shortHour(end % 24)}`)
  }
  return runs.join(' and ')
}

/**
 * The aside's rows. `workspace` undefined → every row unknown. Discount rows say "Off" when
 * governance is not enabled, because `discount-governance` then approves no discount at all
 * (`discounts_disabled`) — a ceiling with nothing under it is not a ceiling worth quoting.
 *
 * The ceiling is read as `resolveOfferPolicy` reads it (F-25): a stored 0, or no number at all,
 * is NO percent discount — never the form's 15% default, which `|| 15` used to put back.
 *
 * The budget is the STORED one only: the form shows a default for a missing budget, but
 * discount-governance budgets against the stored amount (`|| 0`), so with none saved every
 * discount is refused as `budget_exceeded` — the row says that, not merely "none".
 */
export const glanceRows = (
  workspace: Pick<Workspace, 'discountGovernance' | 'smsConfig'> | null | undefined,
  liveGrants: number | null
): GlanceRow[] => {
  const dg = workspace?.discountGovernance as
    | { enabled?: boolean; maxDiscountPercent?: unknown; codeExpiryHours?: number; budget?: { amount?: number; period?: string } }
    | undefined
  const known = workspace != null
  const on = dg?.enabled === true

  const ceiling = dg?.maxDiscountPercent == null ? NaN : Number(dg.maxDiscountPercent)
  const maxDiscount = !known
    ? null
    : !on
      ? 'Off'
      : Number.isFinite(ceiling) && ceiling > 0
        ? `${ceiling}%`
        : 'No % discounts'
  const amount = dg?.budget?.amount
  const budget = !known
    ? null
    : !on
      ? 'Off'
      : typeof amount === 'number' && amount > 0
        ? `$${Math.round(amount / 100).toLocaleString()} ${PERIOD_WORDS[dg?.budget?.period ?? 'monthly'] ?? ''}`.trim()
        : 'None set — no discounts until one is'
  const hours = dg?.codeExpiryHours || 24
  const expiry = !known ? null : !on ? 'Off' : (EXPIRY_LABELS[hours] ?? `${hours} hours`)
  const codes = liveGrants == null ? null : liveGrants === 0 ? 'None live' : String(liveGrants)
  const quiet = !known ? null : textQuietHours(workspace)

  return [
    { label: 'Max discount', value: maxDiscount },
    { label: 'Discount budget', value: budget },
    { label: 'Code expiry', value: expiry },
    { label: 'Codes she may raise', value: codes },
    { label: 'Text quiet hours', value: quiet },
  ]
}

// ── The section nav (MAR-385) ─────────────────────────────────────────────────────────────────
// Kept here, beside the aside's helpers, so `tabs/LimitsTab.tsx` exports only its component.

export type LimitsSection = 'discounts' | 'codes' | 'contact' | 'sms' | 'floor'

export const LIMITS_SECTIONS: { id: LimitsSection; label: string }[] = [
  { id: 'discounts', label: 'Discounts' },
  { id: 'codes', label: 'Shopify codes' },
  { id: 'contact', label: 'How often she writes' },
  { id: 'sms', label: 'Text messages' },
  { id: 'floor', label: 'The fixed floor' },
]

const SECTION_IDS = LIMITS_SECTIONS.map((s) => s.id) as string[]
const DEFAULT_SECTION: LimitsSection = 'discounts'

/**
 * The open section, from the URL alone. A known `section` wins; otherwise the retired Sales tab
 * (`?tab=sales`, whose links meant the mention grant) opens Shopify codes — a missing OR unknown
 * `section` alike — and anything else opens Discounts.
 */
export const sectionFrom = (params: URLSearchParams): LimitsSection => {
  const raw = params.get('section')
  if (raw && SECTION_IDS.includes(raw)) return raw as LimitsSection
  if (params.get('tab') === 'sales') return 'codes'
  return DEFAULT_SECTION
}
